import React, { ReactNode, useContext } from 'react';
import { IntlShape, useIntl } from 'react-intl';

import { PASSWORDLESS_LOGIN, SHADOW_PROJECT_TYPE, SOCIAL_LOGIN } from '../../../constants';
import { SettingsContext } from '../../../contexts/settings';
import { usePaths } from '../../../routes/routesHelper';
import { Breadcrumbs, Tabs } from '../../ui';
import { TabItem } from '../../ui/Tabs/types';
import { TabsWrapper } from './style';

type Props = {
  projectId: string;
  children: ReactNode;
  projectType: string | null;
};

type TabItemProps = {
  projectId: string;
  intl: IntlShape;
  paths: ReturnType<typeof usePaths>;
};

export const tabItemsClassicLogin = ({ projectId, intl, paths }: TabItemProps): TabItem[] => {
  return [
    {
      label: intl.formatMessage({ id: 'configuration-page.menu.item.callback-url' }),
      href: paths.callbackUrlsSettingsPath(projectId)
    },
    {
      label: intl.formatMessage({ id: 'configuration-page.menu.item.username-settings' }),
      href: paths.usernameSettingsPath(projectId)
    },
    {
      label: intl.formatMessage({ id: 'configuration-page.menu.item.password-settings' }),
      href: paths.passwordSettingsPath(projectId)
    },
    {
      label: intl.formatMessage({ id: 'configuration-page.menu.item.email-validation' }),
      href: paths.emailSettingsPath(projectId)
    },
    {
      label: intl.formatMessage({ id: 'configuration-page.menu.item.signup-settings' }),
      href: paths.signUpSettingsPath(projectId)
    },
    {
      label: intl.formatMessage({ id: 'social-connection.tabs.api-settings' }),
      href: paths.apiSettingsPath(projectId)
    }
  ];
};

export const tabItemsSocialLogin = ({ projectId, intl, paths }: TabItemProps): TabItem[] => {
  return [
    {
      label: intl.formatMessage({ id: 'configuration-page.menu.item.callback-url' }),
      href: paths.callbackUrlsSettingsPath(projectId)
    },
    {
      label: intl.formatMessage({ id: 'navigation.authentication-method.social-widget.title' }),
      href: paths.socialConnectionPath(projectId)
    },
    {
      label: intl.formatMessage({ id: 'social-connection.tabs.api-settings' }),
      href: paths.apiSettingsPath(projectId)
    }
  ];
};

export const tabItemsPasswordlessLogin = ({ projectId, intl, paths }: TabItemProps): TabItem[] => {
  return [
    {
      label: intl.formatMessage({ id: 'configuration-page.menu.item.callback-url' }),
      href: paths.callbackUrlsSettingsPath(projectId)
    },
    {
      label: intl.formatMessage({ id: 'settings.tabs.passwordless-settings' }),
      href: paths.passwordlessTypePath(projectId)
    },
    {
      label: intl.formatMessage({ id: 'settings.tabs.email-settings' }),
      href: paths.emailSettingsPath(projectId)
    },
    {
      label: intl.formatMessage({ id: 'social-connection.tabs.api-settings' }),
      href: paths.apiSettingsPath(projectId)
    }
  ];
};

export const tabItemsShadowLogin = ({ projectId, intl, paths }: TabItemProps): TabItem[] => {
  return [
    {
      label: intl.formatMessage({ id: 'configuration-page.menu.item.callback-url' }),
      href: paths.callbackUrlsSettingsPath(projectId)
    },
    {
      label: intl.formatMessage({ id: 'settings.tabs.platform-settings' }),
      href: paths.platformSettingsPath(projectId)
    }
  ];
};

const SettingsTabs = ({ projectId, projectType, children }: Props): JSX.Element | null => {
  const paths = usePaths();
  const { dashboardPath, navigationPath } = paths;
  const intl = useIntl();
  const { currentProject } = useContext(SettingsContext);

  if (!projectId || (!projectType && currentProject?.type !== SHADOW_PROJECT_TYPE)) {
    return null;
  }

  let tabsItems = tabItemsClassicLogin({ projectId, intl, paths });

  if (projectType === SOCIAL_LOGIN) {
    tabsItems = tabItemsSocialLogin({ projectId, intl, paths });
  }

  if (projectType === PASSWORDLESS_LOGIN) {
    tabsItems = tabItemsPasswordlessLogin({ projectId, intl, paths });
  }

  if (!projectType && currentProject?.type === SHADOW_PROJECT_TYPE) {
    tabsItems = tabItemsShadowLogin({ projectId, intl, paths });
  }

  return (
    <>
      <Breadcrumbs
        items={[
          {
            label: intl.messages['dashboard.label'],
            to: dashboardPath()
          },
          {
            label: currentProject?.name,
            to: navigationPath(projectId),
            dataTestId: 'project-name'
          },
          {
            label:
              intl.messages[`settings.crumbs.login-type.${projectType || currentProject?.type}`],
            to: `#`
          }
        ]}
        dataTestId="main-settings"
      />
      <TabsWrapper>
        <Tabs extraRight items={tabsItems} dataTestId="main-settings" />
      </TabsWrapper>
      {children}
    </>
  );
};

export default SettingsTabs;
